/**
 * Date 原型擴展
 * 為 Date 物件添加常用的日期操作方法
 *
 * 注意：此檔案會修改全域 Date 原型，建議在應用程式入口處導入
 */

// ============================================================
// 類型擴展宣告
// ============================================================

/** 時間間隔單位類型 */
export type DateInterval = 'year' | 'quarter' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second';

/** 格式化 token 對應表類型 */
interface FormatTokenMap {
    [key: string]: number;
}

// 擴展 Date 介面
declare global {
    interface Date {
        /**
         * 添加指定的時間間隔
         * @param interval - 時間間隔單位
         * @param units - 要添加的數量（可為負數）
         * @returns 新的 Date 物件，若 interval 無效則返回 undefined
         */
        add(interval: DateInterval | string, units: number): Date | undefined;

        /**
         * 添加指定天數
         * @param days - 要添加的天數（可為負數）
         * @returns 新的 Date 物件
         */
        addDays(days: number): Date;

        /**
         * 格式化日期
         * @param fmt - 格式字串，預設為 "yyyy/MM/dd"
         * @returns 格式化後的字串
         */
        Format(fmt?: string): string;

        /**
         * 取得 ISO 格式字串
         * @returns ISO 格式的日期時間字串
         */
        isoFormat(): string;

        /**
         * 取得當月最後一天的日期
         * @returns 當月最後一天是幾號
         */
        lastDate(): number;
    }
}

// ============================================================
// Date 原型擴展實作
// ============================================================

/**
 * 添加指定的時間間隔
 */
Date.prototype.add = function (interval: DateInterval | string, units: number): Date | undefined {
    const date = this;
    const ret = new Date(this);

    const checkRollover = function (r: Date, d: Date): void {
        if (r.getDate() !== d.getDate()) r.setDate(0);
    };

    switch (String(interval).toLowerCase()) {
        case 'year':
            ret.setFullYear(ret.getFullYear() + units);
            checkRollover(ret, date);
            break;
        case 'quarter':
            ret.setMonth(ret.getMonth() + 3 * units);
            checkRollover(ret, date);
            break;
        case 'month':
            ret.setMonth(ret.getMonth() + units);
            checkRollover(ret, date);
            break;
        case 'week':
            ret.setDate(ret.getDate() + 7 * units);
            break;
        case 'day':
            ret.setDate(ret.getDate() + units);
            break;
        case 'hour':
            ret.setTime(ret.getTime() + units * 3600000);
            break;
        case 'minute':
            ret.setTime(ret.getTime() + units * 60000);
            break;
        case 'second':
            ret.setTime(ret.getTime() + units * 1000);
            break;
        default:
            return undefined;
    }
    return ret;
};

/**
 * 添加指定天數
 */
Date.prototype.addDays = function (days: number): Date {
    const date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
};

/**
 * 格式化日期
 */
Date.prototype.Format = function (fmt?: string): string {
    fmt = fmt || 'yyyy/MM/dd';

    const o: FormatTokenMap = {
        'M+': this.getMonth() + 1, // 月份
        'D+': this.getDate(), // 日
        'd+': this.getDate(), // 日
        'h+': this.getHours(), // 小時
        'm+': this.getMinutes(), // 分
        's+': this.getSeconds(), // 秒
        'q+': Math.floor((this.getMonth() + 3) / 3), // 季度
        S: this.getMilliseconds(), // 毫秒
    };

    if (/(y+)/.test(fmt) || /(Y+)/.test(fmt)) {
        fmt = fmt.replace(RegExp.$1, (this.getFullYear() + '').substr(4 - RegExp.$1.length));
    }

    for (const k in o) {
        if (new RegExp('(' + k + ')').test(fmt)) {
            fmt = fmt.replace(
                RegExp.$1,
                RegExp.$1.length === 1 ? String(o[k]) : ('00' + o[k]).substr(('' + o[k]).length)
            );
        }
    }

    return fmt;
};

/**
 * 取得 ISO 格式字串
 */
Date.prototype.isoFormat = function (): string {
    return this.Format('yyyy/MM/ddThh:mm:ssZ');
};

/**
 * 取得當月最後一天的日期
 */
Date.prototype.lastDate = function (): number {
    return new Date(this.getFullYear(), this.getMonth() + 1, 0).getDate();
};

// ============================================================
// 匯出（用於副作用導入）
// ============================================================

export {};
